import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
  { label: "The Practice", href: "#about", testid: "nav-link-about" },
  { label: "About Us", href: "/about", testid: "nav-link-about-us" },
  { label: "Services", href: "#services", testid: "nav-link-services" },
  { label: "Contact", href: "#contact", testid: "nav-link-contact" },
  { label: "Blog", href: "/blog", testid: "nav-link-blog" },
];

export default function Nav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const goTo = (href) => {
    if (href.startsWith("/")) {
      navigate(href);
      window.scrollTo(0, 0);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/" + href);
    } else {
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-cream/85 backdrop-blur-md border-b hairline-gold border-b" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 lg:px-12">
        <a
          href="#top"
          data-testid="nav-logo"
          onClick={(e) => { e.preventDefault(); if (location.pathname !== "/") navigate("/"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          className="flex items-center"
        >
          <img src="/logo.png" alt="AB & Co Accountant" className="h-11 w-auto lg:h-12" />
        </a>

        <nav className="hidden items-center gap-10 md:flex" aria-label="Primary">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testid}
              onClick={(e) => { e.preventDefault(); goTo(l.href); }}
              className="group relative font-sans text-sm font-medium uppercase tracking-[0.18em] text-navy/80 transition-colors duration-300 hover:text-navy"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
          <button
            data-testid="nav-cta-book"
            onClick={() => goTo("#contact-form")}
            className="border border-navy px-6 py-2.5 font-sans text-sm font-semibold uppercase tracking-[0.18em] text-navy transition-all duration-300 hover:bg-navy hover:text-cream"
          >
            Book a Consultation
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-navy"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="border-t hairline-gold bg-cream px-6 py-6 md:hidden">
          <div className="flex flex-col gap-5">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`${l.testid}-mobile`}
                onClick={(e) => { e.preventDefault(); setOpen(false); goTo(l.href); }}
                className="font-serif text-2xl text-navy"
              >
                {l.label}
              </a>
            ))}
            <button
              data-testid="nav-cta-book-mobile"
              onClick={() => { setOpen(false); goTo("#contact-form"); }}
              className="mt-2 border border-navy px-6 py-3 font-sans text-sm font-semibold uppercase tracking-[0.18em] text-navy"
            >
              Book a Consultation
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
